import { Component } from '@theme/component';

/**
 * A custom dropdown component that supports HTML content in options.
 * Replaces native select to allow styling like strikethrough.
 *
 * @typedef {object} SubscriptionDropdownRefs
 * @property {HTMLElement} button - The dropdown button/trigger.
 * @property {HTMLElement} list - The dropdown list container.
 *
 * @extends Component<SubscriptionDropdownRefs>
 */
class SubscriptionDropdownComponent extends Component {
  requiredRefs = ['button', 'list'];

  connectedCallback() {
    super.connectedCallback();

    this.refs.button.addEventListener('click', this.#toggleDropdown);
    this.refs.list.addEventListener('click', this.#handleOptionClick);
    
    // Close dropdown when clicking outside
    document.addEventListener('click', this.#handleOutsideClick);
    
    // Close on escape key
    document.addEventListener('keydown', this.#handleKeyDown);
    
    // Update button text on initial load (use setTimeout to ensure parent component is ready)
    setTimeout(() => {
      this.#updateButtonText();
    }, 0);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.button.removeEventListener('click', this.#toggleDropdown);
    this.refs.list.removeEventListener('click', this.#handleOptionClick);
    document.removeEventListener('click', this.#handleOutsideClick);
    document.removeEventListener('keydown', this.#handleKeyDown);
  }

  /**
   * Toggles the dropdown open/closed state.
   */
  #toggleDropdown = (event) => {
    event.stopPropagation();
    const isOpen = this.refs.list.hasAttribute('open');
    
    if (isOpen) {
      this.#closeDropdown();
    } else {
      this.#openDropdown();
    }
  };

  /**
   * Opens the dropdown.
   */
  #openDropdown() {
    this.refs.list.setAttribute('open', '');
    this.refs.button.setAttribute('aria-expanded', 'true');
  }

  /**
   * Closes the dropdown.
   */
  #closeDropdown() {
    this.refs.list.removeAttribute('open');
    this.refs.button.setAttribute('aria-expanded', 'false');
  }

  /**
   * Handles clicks on dropdown options.
   */
  #handleOptionClick = (event) => {
    const option = event.target.closest('[data-value]');
    if (!option) return;

    event.stopPropagation();
    
    const value = option.dataset.value;
    const optionText = option.innerHTML;
    
    // Find and update hidden select (it's a sibling or in parent component)
    // Use name attribute since ref is not a valid CSS selector
    const hiddenSelect = this.closest('subscription-options-component')?.querySelector('select[name="subscription_option"]');
    if (hiddenSelect) {
      hiddenSelect.value = value;
      // Dispatch change event on hidden select to trigger subscription change handler
      hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }
    
    // Update button text
    this.refs.button.innerHTML = optionText;
    
    // Update selected state in list
    this.refs.list.querySelectorAll('.subscription-dropdown__option--selected').forEach((opt) => {
      opt.classList.remove('subscription-dropdown__option--selected');
      opt.removeAttribute('aria-selected');
    });
    option.classList.add('subscription-dropdown__option--selected');
    option.setAttribute('aria-selected', 'true');
    
    // Update right label with discount message from selected product
    const subscriptionOptionsComponent = this.closest('subscription-options-component');
    if (subscriptionOptionsComponent && option.dataset.discountMessage) {
      const labelRight = subscriptionOptionsComponent.querySelector('[ref="labelRight"]');
      if (labelRight) {
        // Decode the escaped HTML entity
        const discountMessage = option.dataset.discountMessage
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>');
        labelRight.textContent = discountMessage;
      }
    }
    
    // Close dropdown
    this.#closeDropdown();
  };

  /**
   * Handles clicks outside the dropdown to close it.
   */
  #handleOutsideClick = (event) => {
    if (!this.contains(event.target)) {
      this.#closeDropdown();
    }
  };

  /**
   * Handles keyboard navigation.
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.refs.list.hasAttribute('open')) {
      this.#closeDropdown();
      this.refs.button.focus();
    }
  };

  /**
   * Updates the button text to show the selected option.
   */
  #updateButtonText() {
    // Find the hidden select in the parent component
    // Use name attribute since ref is not a valid CSS selector
    const hiddenSelect = this.closest('subscription-options-component')?.querySelector('select[name="subscription_option"]');
    if (hiddenSelect && hiddenSelect.selectedIndex >= 0) {
      const selectedOption = hiddenSelect.options[hiddenSelect.selectedIndex];
      if (selectedOption) {
        // Get the HTML content from the corresponding dropdown option
        const dropdownOption = this.refs.list.querySelector(`[data-value="${selectedOption.value}"]`);
        if (dropdownOption) {
          this.refs.button.innerHTML = dropdownOption.innerHTML;
          // Mark as selected in the list
          this.refs.list.querySelectorAll('.subscription-dropdown__option--selected').forEach((opt) => {
            opt.classList.remove('subscription-dropdown__option--selected');
            opt.removeAttribute('aria-selected');
          });
          dropdownOption.classList.add('subscription-dropdown__option--selected');
          dropdownOption.setAttribute('aria-selected', 'true');
        } else {
          this.refs.button.textContent = selectedOption.textContent;
        }
        
        // Update right label with discount message from selected product
        const subscriptionOptionsComponent = this.closest('subscription-options-component');
        if (subscriptionOptionsComponent && selectedOption.dataset.discountMessage) {
          const labelRight = subscriptionOptionsComponent.querySelector('[ref="labelRight"]');
          if (labelRight) {
            const discountMessage = selectedOption.dataset.discountMessage
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
              .replace(/&amp;/g, '&')
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>');
            labelRight.textContent = discountMessage;
          }
        }
      }
    } else {
      // If no selection, select the first option
      const firstOption = this.refs.list.querySelector('[data-value]');
      if (firstOption && hiddenSelect) {
        hiddenSelect.value = firstOption.dataset.value;
        this.refs.button.innerHTML = firstOption.innerHTML;
        firstOption.classList.add('subscription-dropdown__option--selected');
        firstOption.setAttribute('aria-selected', 'true');
        
        // Update right label with discount message from first product
        const subscriptionOptionsComponent = this.closest('subscription-options-component');
        if (subscriptionOptionsComponent && firstOption.dataset.discountMessage) {
          const labelRight = subscriptionOptionsComponent.querySelector('[ref="labelRight"]');
          if (labelRight) {
            const discountMessage = firstOption.dataset.discountMessage
              .replace(/&quot;/g, '"')
              .replace(/&#39;/g, "'")
              .replace(/&amp;/g, '&')
              .replace(/&lt;/g, '<')
              .replace(/&gt;/g, '>');
            labelRight.textContent = discountMessage;
          }
        }
        
        // Trigger change event to update price
        hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /**
   * Gets the currently selected value.
   * @returns {string} The selected value.
   */
  getValue() {
    // Use name attribute since ref is not a valid CSS selector
    const hiddenSelect = this.closest('subscription-options-component')?.querySelector('select[name="subscription_option"]');
    return hiddenSelect?.value || '';
  }
}

if (!customElements.get('subscription-dropdown-component')) {
  customElements.define('subscription-dropdown-component', SubscriptionDropdownComponent);
}
